import { useQuery } from "@tanstack/react-query";
import { Mail, Phone, User } from "lucide-react";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { useI18n } from "@project/lib/use-i18n";
import { Stagger, StaggerItem } from "@project/user/components/motion";
import { trusteeService } from "@project/services/user/trustee.service";

export default function TrusteesPage() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["trustees-public"],
    queryFn: () => trusteeService.listEnabled(),
  });

  return (
    <PageShell>
      <PageHero
        eyebrow={t("trust.trustees.hero.eyebrow")}
        title={t("trust.trustees.hero.title")}
        subtitle={t("trust.trustees.hero.subtitle")}
        image={HERO_IMAGES.trustees}
      />
      <div className="mx-auto max-w-6xl px-4 py-12">
        {isLoading ? (
          <p className="text-center text-muted-foreground">Loading…</p>
        ) : !data?.length ? (
          <p className="text-center text-muted-foreground">
            {t("trust.trustees.empty")}
          </p>
        ) : (
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((m, i) => (
              <StaggerItem key={m.id} index={i} className="h-full">
                <div className="h-full rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition hover:shadow-md">
                  <div className="ring-gold/30 mx-auto grid h-16 w-16 place-items-center rounded-full bg-secondary ring-2">
                    <User
                      className="text-primary/50 h-7 w-7"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                  </div>
                  <h3 className="mt-4 font-serif text-lg font-bold text-primary">
                    {m.name}
                  </h3>
                  {m.designation && (
                    <p className="mt-1 text-base text-gold">{m.designation}</p>
                  )}
                  {[m.mobile, m.email].some(Boolean) && (
                    <div className="mt-4 space-y-2 border-t border-border pt-4 text-left">
                      {m.mobile && (
                        <a
                          href={`tel:${m.mobile}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground transition hover:text-primary"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0 text-gold" />
                          <span className="truncate">{m.mobile}</span>
                        </a>
                      )}
                      {m.email && (
                        <a
                          href={`mailto:${m.email}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground transition hover:text-primary"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0 text-gold" />
                          <span className="truncate">{m.email}</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </div>
    </PageShell>
  );
}
